/**
 * Poll Activity Slide
 * Opinion poll question (no correct answer)
 */

import { BarChart3, Check } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';

const POLL_COLORS = [
  'bg-blue-500',
  'bg-orange-500',
  'bg-emerald-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-rose-500',
];

export default function PollSlide({
  slide,
  selectedAnswer,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;
  const options: string[] = content?.options || [];

  return (
    <div className={`max-w-2xl mx-auto mb-8 ${className}`}>
      {/* Activity type badge */}
      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-600 px-4 py-1.5 rounded-full text-sm font-semibold">
          <BarChart3 className="w-4 h-4" />
          Share your opinion
        </div>
      </div>

      <div className="space-y-3">
        {options.map((option: string, index: number) => {
          const isSelected = selectedAnswer === index;
          const barColor = POLL_COLORS[index % POLL_COLORS.length];

          return (
            <motion.button
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.06, duration: 0.3 }}
              whileHover={!hasSubmitted ? { scale: 1.02, x: 4 } : {}}
              whileTap={!hasSubmitted ? { scale: 0.98 } : {}}
              onClick={() => !hasSubmitted && onAnswerChange(index)}
              disabled={hasSubmitted}
              className={`w-full relative p-4 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 overflow-hidden ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-lg ring-2 ring-primary/20'
                  : 'border-border/60 bg-card hover:border-primary/40 hover:shadow-md'
              } ${hasSubmitted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {/* Animated background bar on selection */}
              {isSelected && (
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className={`absolute left-0 top-0 bottom-0 ${barColor} opacity-10 rounded-2xl`}
                />
              )}

              <div className="relative flex items-center gap-4">
                {/* Color dot */}
                <div className={`w-4 h-4 rounded-full ${barColor} shrink-0`} />
                
                {/* Option text */}
                <div className="flex-1 font-medium text-base">{option}</div>

                {/* Check icon when selected */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                    className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0"
                  >
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Selection hint */}
      {!hasSubmitted && selectedAnswer === null && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-sm text-muted-foreground mt-5 animate-pulse"
        >
          Select one option to vote
        </motion.p>
      )}
    </div>
  );
}
